import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { IUserToken, RoutePath } from '@quizzach/interfaces';

/**
 * Custom hook for managing user authentication and handling token-based
 * authorization within the application. The hook checks the validity of
 * a stored token, decodes user information from it, and provides a logout
 * mechanism. If the token is invalid or expired, the user data is cleared.
 */

const isTokenValid = (token: string) => {
	const decodedToken = jwtDecode(token);

	return (
		decodedToken && decodedToken.exp && decodedToken.exp * 1000 > Date.now()
	);
};

export const useUserAuthentication = () => {
	const [token, setToken] = useState(window.localStorage.getItem('userToken'));
	// derive the user synchronously so it's usable on the very first render
	// (e.g. an add-to-cart resumed right after returning from login)
	const [user, setUser] = useState<IUserToken | null>(() =>
		token && isTokenValid(token) ? jwtDecode<IUserToken>(token) : null
	);
	const navigate = useNavigate();

	const handleLogout = (): void => {
		window.localStorage.removeItem('userToken');
		setToken(null);
		navigate(RoutePath.Homepage);
	};

	useEffect(() => {
		if (token && isTokenValid(token)) {
			setUser(jwtDecode(token));
		} else {
			setUser(null);
		}
	}, [token]);

	return { user, handleLogout };
};
